import { useMemo, useState } from 'react';
import { Info } from 'lucide-react';
import { formatMetric } from './panelConfig';
import SortHeader from './components/SortHeader';

const EMPTY = [];

const STATUS = {
  agotado: { label: 'Agotado', cls: 'bg-red-50 text-red-700' },
  discontinuado: { label: 'Discontinuado', cls: 'bg-amber-50 text-amber-700' },
};

const COLUMNS = [
  { key: 'units_sold', label: 'Vendidas', format: 'number' },
  { key: 'revenue', label: 'Revenue', format: 'currency' },
];

function sortRows(rows, key, dir) {
  return [...rows].sort((a, b) => {
    const av = a[key];
    const bv = b[key];
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === 'string' || typeof bv === 'string') {
      return dir === 'asc'
        ? String(av).localeCompare(String(bv))
        : String(bv).localeCompare(String(av));
    }
    return dir === 'asc' ? av - bv : bv - av;
  });
}

function InfoTooltip({ text }) {
  return (
    <span className="group relative inline-flex">
      <Info className="h-3.5 w-3.5 shrink-0 cursor-help text-gray-400" aria-label={text} />
      <span
        role="tooltip"
        className="pointer-events-none absolute left-1/2 top-full z-20 mt-1.5 w-56 -translate-x-1/2 rounded-md bg-gray-900 px-2.5 py-1.5 text-xs font-normal normal-case leading-snug text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100"
      >
        {text}
      </span>
    </span>
  );
}

function Card({ label, value, hint, danger, tooltip }) {
  return (
    <div className="rounded-md bg-gray-50 p-4">
      <div className="flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-gray-500">
        {label}
        {tooltip && <InfoTooltip text={tooltip} />}
      </div>
      <div className={`mt-1 text-2xl font-semibold ${danger ? 'text-red-600' : 'text-gray-900'}`}>{value}</div>
      {hint && <div className="mt-1 text-xs text-gray-400">{hint}</div>}
    </div>
  );
}

function Cell({ col, row }) {
  const v = row[col.key];
  return (
    <td className="px-3 py-2.5 text-right tabular-nums text-gray-900">
      {v == null ? '—' : formatMetric(v, col.format)}
    </td>
  );
}

/**
 * Sección "Catálogo": SKUs que vendieron en el período pero ya no se pueden
 * vender — agotados (stock 0 en el catálogo de hoy) o discontinuados (no
 * aparecen en el catálogo de hoy). El revenue que se está dejando pasar.
 */
export default function CatalogSection({ data, isLoading, isError }) {
  const [sort, setSort] = useState({ key: 'revenue', dir: 'desc' });
  const items = data?.items ?? EMPTY;
  const summary = data?.summary ?? {};
  const rows = useMemo(() => sortRows(items, sort.key, sort.dir), [items, sort]);
  const onSort = (key) =>
    setSort((s) => ({ key, dir: s.key === key && s.dir === 'desc' ? 'asc' : 'desc' }));

  if (isLoading) return <p className="py-8 text-center text-sm text-gray-500">Cargando catálogo…</p>;
  if (isError) return <p className="py-8 text-center text-sm text-red-600">No se pudo cargar el catálogo.</p>;

  return (
    <section className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-gray-900">SKUs sin stock con venta</h2>
        <p className="text-sm text-gray-500">
          Productos agotados o discontinuados que vendieron en el período — revenue que estás dejando pasar.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Card
          label="SKUs sin stock con venta"
          value={formatMetric(summary.sku_count ?? 0, 'number')}
          hint={`${summary.agotados ?? 0} agotados · ${summary.discontinuados ?? 0} discontinuados`}
          tooltip="Cantidad de productos que vendieron en el período seleccionado pero hoy están agotados (stock 0) o discontinuados (ya no figuran en el catálogo)."
        />
        <Card
          label="Revenue en riesgo"
          value={formatMetric(summary.revenue_at_risk ?? 0, 'currency')}
          danger
          tooltip="Facturación de esos productos en el período seleccionado. Es la venta que podrías estar perdiendo por no tener stock."
        />
        <Card
          label="% Revenue en riesgo"
          value={formatMetric(summary.revenue_at_risk_pct, 'percent')}
          danger
          tooltip="Del total facturado en el período, qué porción proviene de productos que ya no tenés en stock (revenue en riesgo ÷ revenue total del período)."
        />
        <Card
          label="Unidades vendidas"
          value={formatMetric(summary.units_at_risk ?? 0, 'number')}
          tooltip="Unidades vendidas de esos productos durante el período seleccionado."
        />
      </div>

      {rows.length === 0 ? (
        <p className="py-8 text-center text-sm text-gray-500">
          Sin quiebres de stock en el rango — los productos vendidos tienen stock.
        </p>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          <div className="max-h-[52rem] overflow-auto">
            <table className="w-full min-w-[480px] border-collapse text-sm">
              <thead>
                <tr className="sticky top-0 z-10 border-b border-gray-200 bg-white text-xs uppercase tracking-wide text-gray-500">
                  <SortHeader sortKey="product_name" sort={sort} onSort={onSort} align="left">
                    Producto
                  </SortHeader>
                  {COLUMNS.map((c) => (
                    <SortHeader key={c.key} sortKey={c.key} sort={sort} onSort={onSort}>
                      {c.label}
                    </SortHeader>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((it) => {
                  const st = STATUS[it.status] ?? STATUS.agotado;
                  return (
                    <tr key={it.sku} className="border-b border-gray-100 last:border-0 hover:bg-gray-50">
                      <td className="px-3 py-2.5">
                        <div className="flex items-center gap-2">
                          <span className="text-gray-900">{it.product_name || it.sku}</span>
                          <span className={`rounded px-1.5 py-0.5 text-xs font-medium ${st.cls}`}>{st.label}</span>
                        </div>
                        <div className="text-xs text-gray-400">{it.sku}</div>
                      </td>
                      {COLUMNS.map((c) => (
                        <Cell key={c.key} col={c} row={it} />
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </section>
  );
}
